import React, { useEffect } from "react";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { advertorialData as d } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
  };
};

// Taboola appends its click id to the landing URL as ?tblci=...
// Persist it for the session so it survives in-page navigation
const getTaboolaClickId = () => {
  const fromUrl = new URLSearchParams(window.location.search).get("tblci");
  if (fromUrl) {
    sessionStorage.setItem("tblci", fromUrl);
    return fromUrl;
  }
  return sessionStorage.getItem("tblci") || null;
};

const genId = () =>
  window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getSessionId = () => {
  let sid = sessionStorage.getItem("visit_session_id");
  if (!sid) {
    sid = genId();
    sessionStorage.setItem("visit_session_id", sid);
  }
  return sid;
};

// Appends cta + click_id (+ tblci if present) params to the outbound URL (keeping any #hash at the end)
const buildOutboundUrl = (href, buttonId, clickId) => {
  const [base, hash] = href.split("#");
  const sep = base.includes("?") ? "&" : "?";
  const tblci = getTaboolaClickId();
  let url = `${base}${sep}cta=${encodeURIComponent(buttonId)}&click_id=${encodeURIComponent(clickId)}`;
  if (tblci) url += `&tblci=${encodeURIComponent(tblci)}`;
  return `${url}${hash ? "#" + hash : ""}`;
};

const trackClick = (buttonId) => {
  const utm = getUtmParams();
  const clickId = genId();
  // Fire-and-forget: never block navigation to the CTA link
  axios
    .post(`${API}/track/click`, {
      button_id: buttonId,
      click_id: clickId,
      session_id: getSessionId(),
      taboola_click_id: getTaboolaClickId(),
      ...utm,
    })
    .catch(() => {});
  // GA4 event for Google Analytics reporting
  if (typeof window.gtag === "function") {
    window.gtag("event", "cta_click", {
      button_id: buttonId,
      click_id: clickId,
      utm_source: utm.utm_source || "direct",
      utm_medium: utm.utm_medium || "(none)",
      utm_campaign: utm.utm_campaign || "(none)",
    });
  }
  return clickId;
};

const B = ({ children }) => <strong className="font-bold text-black/[.87]">{children}</strong>;

const CtaButton = ({ href, children, wide, trackId, subtitle }) => (
  <div className={`my-8 ${wide ? "" : "text-center"}`}>
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        const clickId = trackClick(trackId);
        // Rewrite href just-in-time so the outbound URL carries cta + click_id for CRM attribution.
        // Build from the CURRENT href (not the static prop) so GA4's cross-domain _gl linker
        // param (added by gtag on mousedown) is preserved. Guard against double-append.
        const current = e.currentTarget.getAttribute("href") || href;
        const base = current.includes("cta=") ? href : current;
        e.currentTarget.href = buildOutboundUrl(base, trackId, clickId);
      }}
      className={`cta-btn inline-block bg-[#11a71b] text-white rounded-full text-center ${
        wide ? "w-full px-8 py-5" : "font-bold text-base leading-snug px-7 py-[14px]"
      }`}
    >
      {wide ? (
        <span className="flex flex-col items-center gap-1">
          <span className="font-lato font-bold text-lg sm:text-xl leading-tight flex items-center gap-2">
            {children}
            <ArrowRight size={20} className="shrink-0 cta-arrow" aria-hidden="true" />
          </span>
          {subtitle && (
            <span className="font-normal text-sm sm:text-[15px] text-white/85 leading-snug">
              {subtitle}
            </span>
          )}
        </span>
      ) : (
        children
      )}
    </a>
  </div>
);

const ArticleImage = ({ src, alt }) => (
  <div className="my-6">
    <img src={src} alt={alt} className="w-full rounded-sm block" loading="lazy" />
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-lato font-semibold text-[26px] sm:text-[30px] leading-tight text-black/[.87] mt-10 mb-4">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="text-base leading-relaxed text-black/[.75] mb-4">{children}</p>
);

export default function AdvertorialPage() {
  // Page-view / arrival tracking: fire once per session on load (captures tblci + UTM)
  useEffect(() => {
    if (sessionStorage.getItem("pageview_sent")) return;
    sessionStorage.setItem("pageview_sent", "1");
    const utm = getUtmParams();
    axios
      .post(`${API}/track/pageview`, {
        session_id: getSessionId(),
        taboola_click_id: getTaboolaClickId(),
        path: window.location.pathname,
        referrer: document.referrer || null,
        ...utm,
      })
      .catch(() => {});
  }, []);

  // Scroll depth tracking: fire each milestone (25/50/75/90/100) once per visit
  useEffect(() => {
    const fired = new Set();
    const milestones = [25, 50, 75, 90, 100];
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const percent =
        scrollable <= 0 ? 100 : Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      milestones.forEach((m) => {
        if (percent >= m && !fired.has(m)) {
          fired.add(m);
          if (typeof window.gtag === "function") {
            window.gtag("event", "scroll_depth", {
              percent: m,
              page_path: window.location.pathname,
            });
          }
          axios
            .post(`${API}/track/scroll`, {
              session_id: getSessionId(),
              depth: m,
              path: window.location.pathname,
            })
            .catch(() => {});
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // catch short viewports where 25% is already visible
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Article card */}
      <main className="max-w-[792px] mx-auto mt-8 mb-0 bg-white sm:shadow-sm">
        <article className="px-5 sm:px-8 py-8">
          <h1 className="font-lato font-semibold text-[30px] sm:text-[36px] leading-tight text-black/[.87] mb-4">
            {d.headline}
          </h1>

          <div className="mb-2">
            <img
              src={d.heroImage.src}
              alt={d.heroImage.alt}
              className="w-full rounded-sm block"
            />
          </div>

          <H2>Why Traders Are Failing Daily</H2>

          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
            <p className="text-sm text-gray-600">
              By{" "}
              <a
                href={d.ctaLinks.main}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2563eb] font-semibold hover:underline"
              >
                {d.author}
              </a>
            </p>
            <p className="text-sm italic text-gray-500">{d.date}</p>
          </div>

          <P>The majority of traders you see online trade based upon strategies that don't work.</P>
          <P>Trendlines, Price Patterns and Indicators. They overtrade and follow signal groups.</P>
          <P>This is not a consistent path to become profitable with your trading career.</P>
          <P><B>Especially when the markets are built against you.</B></P>
          <P>They're designed for you to fail.</P>
          <P>
            A collective named <B>Smart Money</B> is constantly manipulating Retail traders to take
            their hard earned money.
          </P>
          <P>
            Within <B>The Matrix Unlocked</B> we show you exactly what <B>Smart Money</B> is doing
            behind the scenes. Then you're no longer fighting against them, you join them and profit
            from the masses.
          </P>

          <CtaButton href={d.ctaLinks.main} trackId="watch_video">Watch A Short Explanatory Video</CtaButton>

          <H2>
            How <B>Smart Money</B> Operates in The Markets
          </H2>
          <P>
            <B>
              Smart Money is a collective of entities such as Central Banks, Hedge Funds and Family
              Offices. It's basically the very wealthy individuals.
            </B>
          </P>
          <P>
            Their goal is to extract profits from the working men or women who trade or invest within
            the markets. Their method of accomplishing this is by trapping the common trader into
            believing false narratives or ideas about the marketplace.
          </P>
          <P>
            <B>Smart Money</B> sets up these false beliefs by making manipulative market movements
            take place which lead the Retail trader or investor into believing the market will do A
            whilst <B>Smart Money</B> their plan is B.
          </P>
          <P>
            Meaning <B>Smart Money</B> is constantly working against Retail traders to profit from
            them.
          </P>
          <P>
            <B>Smart Money</B> uses algorithms (High-Frequency Trading Algorithms) to constantly move
            the markets.
          </P>
          <P>They have an automatic approach to move the markets.</P>

          <ArticleImage src={d.images.algorithms.src} alt={d.images.algorithms.alt} />

          <H2>How The Algorithms Work</H2>
          <P>
            The algorithms <B>Smart Money</B> uses are focused on making the markets move according
            to <B>Smart Money</B> their plan.
          </P>
          <P>Their main objective is to constantly extract profits from the masses.</P>
          <P>
            These algorithms work based upon <B>Time</B> and <B>Price</B>. They leave behind
            footprints in the markets.
          </P>
          <P>
            These are so called <B>Algorithmic Signatures</B>. Once you recognize these{" "}
            <B>Algorithmic Signatures</B> you can anticipate their next move.
          </P>
          <P>
            And once you become good at recognizing these <B>Algorithmic Signatures</B> you are able
            to anticipate what's likely going to be happening next.
          </P>

          <ArticleImage src={d.images.signatures.src} alt={d.images.signatures.alt} />

          <CtaButton href={d.ctaLinks.typeform} trackId="learn_algorithms">Learn How The Algorithms Work</CtaButton>

          <H2>The Downside of Not Knowing That This Is Happening Behind The Scenes</H2>
          <P>
            Millions of market participants are unaware of the fact that this is happening behind the
            scenes. This provides an unfair playing field of those in the Know and those that don't.
          </P>
          <P>
            If you are not aware of how <B>Smart Money</B> operates you will always consistently keep
            on losing money.
          </P>
          <P>
            And take a guess where that money lands... In the pockets of <B>Smart Money</B>.
          </P>
          <P>
            Now imagine if you never came across this article. Wouldn't you feel a fool if you
            continued participating in the markets as you have been without knowing how{" "}
            <B>Smart Money</B> actually operates. Day by day. Week by week.
          </P>
          <P>
            The system is built against you, for you to fail. You cannot change the system. You can
            only operate according to its rules if longevity and consistency is your objective.
          </P>

          <H2>
            <B>The Matrix Unlocked</B>
          </H2>
          <P>
            Nearly 2 years ago <B>The Matrix Unlocked</B> was founded.
          </P>
          <P>
            A collective of traders that Know how <B>Smart Money</B> operates decided to share the
            underlying frameworks hidden in plain sight.
          </P>

          <ArticleImage src={d.images.collective.src} alt={d.images.collective.alt} />

          <P>
            And only by using two variables. The only two variables that these algorithms adhere by.
            <br />
            <B>Time</B> and <B>Price</B>.
          </P>
          <P>
            By understanding the key <B>Algorithmic Signatures</B> which <B>Smart Money</B> their
            algorithms leave behind in both <B>Time</B> as <B>Price</B>, one can anticipate what
            their plan is.
          </P>
          <P>
            Now imagine what happens when you're no longer fighting against <B>Smart Money</B> and
            decide to operate as they are.
          </P>
          <P>
            Do you understand that the step towards long term consistency is then all of the sudden
            within reach?
          </P>

          <CtaButton
            href={d.ctaLinks.typeform}
            wide
            trackId="apply_now"
            subtitle="See if you qualify to become part of those in the Know"
          >
            Apply for The Matrix Unlocked
          </CtaButton>

          {/* Article footer */}
          <footer className="text-sm text-gray-600 pt-2">
            {d.footer.copyright} <strong>{d.footer.brand}</strong>. {d.footer.rights} |{" "}
            <a
              href={d.ctaLinks.about}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2563eb] hover:underline"
            >
              About Us
            </a>{" "}
            |{" "}
            <a
              href={d.ctaLinks.privacy}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2563eb] hover:underline"
            >
              Privacy Policy
            </a>{" "}
            |{" "}
            <a
              href={d.ctaLinks.terms}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2563eb] hover:underline"
            >
              Terms of Use
            </a>
          </footer>
        </article>
      </main>

      <div className="h-12" />
    </div>
  );
}
